import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  restoreGame,
  keyboardStates,
  isWordleError,
  WORD_LENGTH,
  MAX_ATTEMPTS,
  type WordMeaningResponse,
} from '@familyhub/shared';
import { useSession } from '../session';
import { api } from '../api/client';
import { Sheet } from '../platform/Sheet';
import { wdb, type WordleGameRecord } from './db';
import { submitDailyGuess } from './actions';
import { householdTarget } from './target';
import { spelledAnswer } from './words';
import { todayPuzzleDate, formatPuzzleDate } from './datetime';
import { computeStats, type WordleStats } from './stats';
import { duelOutcome, headToHead, leaderOf, type DuelResult } from './duel';
import { memberColor } from './people';
import { Board } from './Board';
import { Keyboard } from './Keyboard';
import { ChartIcon, ShareIcon, CrownIcon } from './icons';

/**
 * The Wordle module — "Het Woord". The household's shared daily word game: both
 * members play their own board against the same daily answer, then it becomes a
 * duel — today's winner, and a running head-to-head over every shared day.
 *
 * Reads come from the synced mirror (`wdb`), never the network; a guess is an
 * optimistic local write replayed through the outbox. The engine, lexicon, daily
 * selection (all `@familyhub/shared`) and the derived stats/duel are the game.
 */

/** A player's one-line status for today — never their letters (no spoiler).
 *  The guess count is itself a spoiler: knowing your partner solved it in three
 *  would change how you play, so while the viewer is still going (`reveal` false)
 *  a partner's row says only "bezig"/"klaar", never a number. Once the viewer has
 *  finished — and always for your own row — the full result and its count show. */
function playerStatus(game: WordleGameRecord | undefined, reveal: boolean): string {
  if (!game) return 'nog niet begonnen';
  const finished = game.status === 'won' || game.status === 'lost';
  if (!reveal) return finished ? 'klaar' : 'bezig';
  if (game.status === 'won') return `opgelost in ${game.guesses.length}/${MAX_ATTEMPTS}`;
  if (game.status === 'lost') return 'niet gehaald';
  return `bezig (${game.guesses.length}/${MAX_ATTEMPTS})`;
}

/** A colour-only grid — the classic share pattern, revealed once the viewer is done. */
function MiniGrid({ game }: { game: WordleGameRecord }) {
  const state = restoreGame(game.target_word, game.guesses);
  return (
    <div className="wl-mini" aria-hidden>
      {state.evaluations.map((row, r) => (
        <div className="wl-mini-row" key={r}>
          {row.map((s, c) => (
            <span className="wl-mini-cell" data-state={s} key={c} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** One side of today's duel: name, a crown if they won it, status, and — once
 *  the viewer has finished — the spoiler-safe colour grid. */
function DuelRow({
  name,
  color,
  game,
  crown,
  reveal,
}: {
  name: string;
  color: string;
  game: WordleGameRecord | null;
  crown: boolean;
  reveal: boolean;
}) {
  const finished = !!game && (game.status === 'won' || game.status === 'lost');
  return (
    <div className="wl-partner">
      <span className="wl-dot" style={{ background: color }} aria-hidden />
      <span className="wl-partner-name">{name}</span>
      {crown && (
        <span className="wl-crown" style={{ color }} role="img" aria-label="wint vandaag">
          <CrownIcon />
        </span>
      )}
      <span className="wl-partner-status">{playerStatus(game ?? undefined, reveal)}</span>
      {reveal && finished && <MiniGrid game={game} />}
    </div>
  );
}

const fmtAvg = (n: number | null): string => (n === null ? '—' : n.toFixed(1));

type Better = 'high' | 'low' | 'none';
function lead(a: number | null, b: number | null, better: Better): 'you' | 'them' | 'even' {
  if (better === 'none') return 'even';
  if (a === null && b === null) return 'even';
  if (a === null) return 'them';
  if (b === null) return 'you';
  if (a === b) return 'even';
  const youAhead = better === 'high' ? a > b : a < b;
  return youAhead ? 'you' : 'them';
}

/** The head-to-head stats table — each metric with both boards' values, the
 *  sharper one inked and the other dimmed (colour never carries it alone). */
function CompareTable({
  you,
  them,
  youColor,
  themColor,
  partnerName,
}: {
  you: WordleStats;
  them: WordleStats;
  youColor: string;
  themColor: string;
  partnerName: string;
}) {
  const rows: { label: string; you: string; them: string; who: 'you' | 'them' | 'even' }[] = [
    { label: 'Gespeeld', you: String(you.played), them: String(them.played), who: 'even' },
    {
      label: 'Winst',
      you: `${you.winRate}%`,
      them: `${them.winRate}%`,
      who: lead(you.winRate, them.winRate, 'high'),
    },
    {
      label: 'Gem. pogingen',
      you: fmtAvg(you.averageGuesses),
      them: fmtAvg(them.averageGuesses),
      who: lead(you.averageGuesses, them.averageGuesses, 'low'),
    },
    {
      label: 'Reeks',
      you: String(you.currentStreak),
      them: String(them.currentStreak),
      who: lead(you.currentStreak, them.currentStreak, 'high'),
    },
    {
      label: 'Beste reeks',
      you: String(you.maxStreak),
      them: String(them.maxStreak),
      who: lead(you.maxStreak, them.maxStreak, 'high'),
    },
  ];
  return (
    <table className="wl-compare">
      <thead>
        <tr>
          <th scope="col" />
          <th scope="col">
            <span className="wl-dot" style={{ background: youColor }} aria-hidden /> Jij
          </th>
          <th scope="col">
            <span className="wl-dot" style={{ background: themColor }} aria-hidden /> {partnerName}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label}>
            <th scope="row">{r.label}</th>
            <td className={r.who === 'you' ? 'is-lead' : undefined}>{r.you}</td>
            <td className={r.who === 'them' ? 'is-lead' : undefined}>{r.them}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function WordleScreen() {
  const session = useSession();
  const userId = session.status === 'in' ? session.userId : '';
  const me = session.status === 'in' ? session.me : null;
  const members = useMemo(() => me?.members ?? [], [me]);
  const memberIds = useMemo(() => members.map((m) => m.id), [members]);
  const partner = useMemo(() => members.find((m) => m.id !== userId) ?? null, [members, userId]);

  const puzzleDate = useMemo(() => todayPuzzleDate(), []);

  const allGames =
    useLiveQuery(() => wdb.games.filter((g) => !g.deleted_at).toArray(), [], []) ?? [];
  const gamesOf = useCallback(
    (id: string | undefined | null) => (id ? allGames.filter((g) => g.created_by === id) : []),
    [allGames],
  );

  const todayGames = useMemo(
    () => allGames.filter((g) => g.puzzle_date === puzzleDate),
    [allGames, puzzleDate],
  );
  const myGame = todayGames.find((g) => g.created_by === userId) ?? null;
  const partnerGame = partner ? todayGames.find((g) => g.created_by === partner.id) ?? null : null;

  const [current, setCurrent] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [showStats, setShowStats] = useState(false);

  // Not `LEXICON.getDailyTarget` directly: the word lists ship in the bundle, so
  // whoever in the household started today's puzzle first decides what it is.
  const dailyTarget = useMemo(
    () => householdTarget(todayGames, userId, puzzleDate),
    [todayGames, userId, puzzleDate],
  );
  const state = useMemo(
    () => restoreGame(dailyTarget, myGame?.guesses ?? []),
    [dailyTarget, myGame?.guesses],
  );
  const over = state.status !== 'in_progress';
  const letters = useMemo(() => keyboardStates(state), [state]);

  // The word's meaning, read once the game ends (won or lost) — see
  // `wordle/meaning.ts`. Best-effort: nl.wiktionary.org having nothing for a
  // word, or no signal at all, just means the block stays hidden.
  const [meaning, setMeaning] = useState<WordMeaningResponse | null>(null);
  useEffect(() => {
    if (!over) {
      setMeaning(null);
      return;
    }
    let cancelled = false;
    api<WordMeaningResponse>(
      `/api/v1/wordle/meaning?word=${encodeURIComponent(spelledAnswer(dailyTarget))}`,
    )
      .then((res) => {
        if (!cancelled) setMeaning(res);
      })
      .catch(() => {
        // No connection or nothing found — the block simply never appears.
      });
    return () => {
      cancelled = true;
    };
  }, [over, dailyTarget]);

  const flash = useCallback((msg: string) => {
    setMessage(msg);
    setShake(true);
    window.setTimeout(() => setShake(false), 450);
    window.setTimeout(() => setMessage((m) => (m === msg ? null : m)), 1500);
  }, []);

  const submit = useCallback(async () => {
    if (state.status !== 'in_progress') return;
    if (current.length !== WORD_LENGTH) {
      flash('Te weinig letters');
      return;
    }
    try {
      await submitDailyGuess(userId, current);
      setCurrent('');
    } catch (err) {
      if (isWordleError(err) && err.code === 'INVALID_WORD') flash('Niet in de woordenlijst');
    }
  }, [state, current, userId, flash]);

  const type = useCallback(
    (letter: string) => {
      if (over) return;
      setCurrent((c) => (c.length < WORD_LENGTH ? c + letter : c));
    },
    [over],
  );
  const backspace = useCallback(() => setCurrent((c) => c.slice(0, -1)), []);

  // Physical keyboard, through a ref so the listener is attached once.
  const handlers = useRef({ submit, type, backspace });
  handlers.current = { submit, type, backspace };
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        void handlers.current.submit();
      } else if (e.key === 'Backspace') {
        handlers.current.backspace();
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        handlers.current.type(e.key.toUpperCase());
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const shareText = (): string => {
    const n = state.status === 'won' ? String(state.guesses.length) : 'X';
    const grid = state.evaluations
      .map((row) => row.map((s) => (s === 'exact' ? '🟩' : s === 'present' ? '🟨' : '⬛')).join(''))
      .join('\n');
    return `Het Woord ${puzzleDate} ${n}/${MAX_ATTEMPTS}\n\n${grid}`;
  };
  const share = async () => {
    try {
      await navigator.clipboard.writeText(shareText());
      flash('Gekopieerd!');
    } catch {
      flash('Kopiëren lukte niet');
    }
  };

  const yourStats = useMemo(() => computeStats(gamesOf(userId)), [gamesOf, userId]);
  const partnerStats = useMemo(() => computeStats(gamesOf(partner?.id)), [gamesOf, partner]);
  const stand = useMemo(
    () => (partner ? headToHead(allGames, userId, partner.id) : null),
    [allGames, userId, partner],
  );
  const todayDuel: DuelResult = duelOutcome(myGame ?? undefined, partnerGame ?? undefined);
  const maxDist = Math.max(1, ...yourStats.distribution);
  const todayWinRow = myGame?.status === 'won' ? myGame.guesses.length : null;

  const youColor = memberColor(userId, memberIds) ?? 'var(--text-dim)';
  const partnerColor = partner
    ? memberColor(partner.id, memberIds) ?? 'var(--text-dim)'
    : 'var(--text-dim)';

  const standLead = stand ? leaderOf(stand) : 'even';
  const standCaption = !stand || stand.decided === 0
    ? 'Nog geen onderlinge duels'
    : standLead === 'even'
      ? 'Precies gelijk'
      : `${standLead === 'you' ? 'Jij' : partner?.display_name} staat voor`;

  const verdict = ((): string | null => {
    if (todayDuel === 'pending' || !partner) return null;
    if (todayDuel === 'you') return 'Jij wint vandaag.';
    if (todayDuel === 'them') return `${partner.display_name} wint vandaag.`;
    if (myGame?.status === 'won' && partnerGame?.status === 'won') {
      return `Gelijkspel — allebei in ${myGame.guesses.length}.`;
    }
    return 'Allebei niet gehaald vandaag.';
  })();

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/" aria-label="Naar het menu">
          ‹ Hub
        </Link>
        <h1>Het Woord</h1>
        <button
          className="btn btn-quiet"
          aria-label="Statistieken"
          aria-pressed={showStats}
          onClick={() => setShowStats((s) => !s)}
        >
          <ChartIcon />
        </button>
      </div>

      <main className="wl-main">
        <p className="wl-date">{formatPuzzleDate(puzzleDate)}</p>

        <div className="wl-toast-wrap" aria-live="polite">
          {message && <div className="wl-toast">{message}</div>}
        </div>

        <Board state={state} current={current} shake={shake} />

        {over && (
          <div className={`wl-result ${state.status}`} role="status">
            {state.status === 'won' ? (
              <strong>Opgelost in {state.guesses.length}/{MAX_ATTEMPTS}!</strong>
            ) : (
              <strong>
                Helaas — het woord was{' '}
                <span className="wl-answer">{spelledAnswer(state.target).toUpperCase()}</span>
              </strong>
            )}
            <button className="btn btn-quiet wl-share" onClick={share}>
              <ShareIcon /> Deel
            </button>
          </div>
        )}

        {over && meaning && meaning.definitions.length > 0 && (
          <div className="wl-meaning">
            <h3 className="wl-h3">Betekenis</h3>
            {meaning.form_of && <p className="wl-meaning-form">{meaning.form_of}</p>}
            <ol className="wl-meaning-list">
              {meaning.definitions.map((def, i) => (
                <li key={i}>{def}</li>
              ))}
            </ol>
            {meaning.source_url && (
              <a
                className="wl-meaning-source"
                href={meaning.source_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Bron: Wiktionary
              </a>
            )}
          </div>
        )}
        {/* Wiktionary answered and had nothing: say so and point at a dictionary,
            rather than leave an answer with no explanation. (A failed request
            leaves `meaning` null — no signal means a link would not work either.) */}
        {over && meaning && meaning.definitions.length === 0 && (
          <div className="wl-meaning">
            <h3 className="wl-h3">Betekenis</h3>
            <p className="wl-meaning-none">Wiktionary kent dit woord niet.</p>
            <a
              className="wl-meaning-source"
              href={`https://www.woorden.org/woord/${encodeURIComponent(spelledAnswer(dailyTarget))}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Zoek op in woorden.org
            </a>
          </div>
        )}

        <Keyboard
          letterStates={letters}
          onKey={type}
          onEnter={() => void submit()}
          onBackspace={backspace}
          disabled={over}
        />

        {partner && (
          <section className="wl-panel">
            <h2 className="wl-h2">Vandaag</h2>
            <div className="wl-today">
              <DuelRow
                name="Jij"
                color={youColor}
                game={myGame}
                crown={todayDuel === 'you'}
                reveal
              />
              <DuelRow
                name={partner.display_name}
                color={partnerColor}
                game={partnerGame}
                crown={todayDuel === 'them'}
                reveal={over}
              />
            </div>
            {verdict && (
              <p className="wl-verdict" data-result={todayDuel} role="status">
                {verdict}
              </p>
            )}

            <div className="wl-stand">
              <h3 className="wl-h3">Onderlinge stand</h3>
              <div className="wl-score" role="group" aria-label="Onderlinge stand">
                <div className="wl-score-side">
                  <span className="wl-score-name">
                    <span className="wl-dot" style={{ background: youColor }} aria-hidden /> Jij
                  </span>
                  <span className={`wl-score-n${standLead === 'you' ? ' is-lead' : ''}`}>
                    {stand?.you ?? 0}
                  </span>
                </div>
                <span className="wl-score-dash" aria-hidden>
                  –
                </span>
                <div className="wl-score-side">
                  <span className="wl-score-name">
                    <span className="wl-dot" style={{ background: partnerColor }} aria-hidden />{' '}
                    {partner.display_name}
                  </span>
                  <span className={`wl-score-n${standLead === 'them' ? ' is-lead' : ''}`}>
                    {stand?.them ?? 0}
                  </span>
                </div>
              </div>
              <p className="wl-score-cap">
                {standCaption}
                {stand && stand.draws > 0 && (
                  <span className="wl-score-draws"> · {stand.draws}× gelijk</span>
                )}
              </p>
            </div>
          </section>
        )}

      </main>

      {showStats && (
        <Sheet title="Statistieken" onClose={() => setShowStats(false)}>
          {partner ? (
            <CompareTable
              you={yourStats}
              them={partnerStats}
              youColor={youColor}
              themColor={partnerColor}
              partnerName={partner.display_name}
            />
          ) : (
            <div className="wl-stat-tiles">
              <div className="wl-stat">
                <span className="wl-stat-n">{yourStats.played}</span>
                <span className="wl-stat-l">Gespeeld</span>
              </div>
              <div className="wl-stat">
                <span className="wl-stat-n">{yourStats.winRate}%</span>
                <span className="wl-stat-l">Gewonnen</span>
              </div>
              <div className="wl-stat">
                <span className="wl-stat-n">{yourStats.currentStreak}</span>
                <span className="wl-stat-l">Reeks</span>
              </div>
              <div className="wl-stat">
                <span className="wl-stat-n">{yourStats.maxStreak}</span>
                <span className="wl-stat-l">Beste reeks</span>
              </div>
            </div>
          )}
          <div className="wl-stats-dist">
            <h3 className="wl-h3">Jouw verdeling</h3>
            <div className="wl-dist" aria-label="Verdeling van pogingen">
              {yourStats.distribution.map((count, i) => {
                const isToday = todayWinRow === i + 1;
                return (
                  <div className={`wl-dist-row${isToday ? ' is-today' : ''}`} key={i}>
                    <span className="wl-dist-k">{i + 1}</span>
                    <span
                      className={`wl-dist-bar${isToday ? ' is-today' : ''}`}
                      style={{ width: `${(count / maxDist) * 100}%` }}
                      data-empty={count === 0 ? 'true' : undefined}
                    >
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Sheet>
      )}
    </div>
  );
}
